import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchCurrentUser } from "../../lib/auth";
import { fetchOrderInvoice, type OrderInvoiceResponse } from "../../lib/catalog";
import { formatCurrency, formatDateTime } from "../../lib/utils";

function servedByLabel(firstName: string | null, lastName: string | null) {
  const explicitLastName = lastName?.trim();
  if (explicitLastName) {
    return explicitLastName;
  }

  const fallback = firstName?.trim();
  if (!fallback) {
    return "Sales";
  }

  const parts = fallback.split(/\s+/);
  return parts[parts.length - 1] || "Sales";
}

export function SalesReceiptPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [data, setData] = useState<OrderInvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [servedBy, setServedBy] = useState("Sales");

  useEffect(() => {
    let ignore = false;

    async function loadReceipt() {
      if (!orderId) {
        setError("Missing order reference.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams();
        query.set("id", orderId);
        const result = await fetchOrderInvoice(query);
        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load this receipt.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadReceipt();
    return () => {
      ignore = true;
    };
  }, [orderId]);

  useEffect(() => {
    if (!loading && data) {
      const printTimer = window.setTimeout(() => {
        window.print();
      }, 250);

      return () => {
        window.clearTimeout(printTimer);
      };
    }
  }, [data, loading]);

  useEffect(() => {
    let ignore = false;

    async function loadCurrentUser() {
      try {
        const current = await fetchCurrentUser();
        if (!ignore && current.user) {
          setServedBy(servedByLabel(current.user.firstName, current.user.lastName));
        }
      } catch {
        if (!ignore) {
          setServedBy("Sales");
        }
      }
    }

    void loadCurrentUser();
    return () => {
      ignore = true;
    };
  }, []);

  const qrValue =
    data && orderId
      ? encodeURIComponent(
          `Novatech Receipt #${data.order.id}\nReference: ${data.order.trackingNumber || "N/A"}\nOrder: ${orderId}\nTotal: ${data.order.total}\nPaid: ${data.order.amountPaid}\nBalance: ${data.order.balance}`
        )
      : null;
  const qrImageUrl = qrValue ? `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${qrValue}` : null;

  return (
    <section className="mx-auto flex min-h-screen w-full items-start justify-center bg-nv-paper px-3 py-6">
      {loading ? (
        <div className="glass-panel rounded-[24px] p-6 text-sm text-nv-slate">Loading receipt...</div>
      ) : error || !data ? (
        <div className="glass-panel rounded-[24px] p-6">
          <h1 className="text-2xl font-semibold tracking-tight text-nv-ink">Receipt unavailable</h1>
          <p className="mt-3 text-sm text-nv-slate">{error || "This receipt could not be found."}</p>
          <Link className="brand-button mt-5 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/sales/orders">
            Back to orders
          </Link>
        </div>
      ) : (
        <div className="receipt-shell w-full max-w-[58mm] rounded-[18px] border border-black/10 bg-white p-4 text-black shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
          <div className="receipt-actions mb-4 flex items-center justify-between gap-3">
            <Link className="rounded-full border border-black/10 px-3 py-2 text-xs font-semibold" to="/sales/orders">
              Back
            </Link>
            <button className="rounded-full border border-black/10 px-3 py-2 text-xs font-semibold" onClick={() => window.print()} type="button">
              Print / download
            </button>
          </div>

          <div className="border-b border-dashed border-black/20 pb-3 text-center">
            <img alt="Novatech" className="mx-auto h-10 w-10 object-contain" src="/logo/logo.jpg" />
            <p className="mt-2 text-base font-bold uppercase tracking-[0.18em]">Novatech</p>
            <p className="mt-1 text-[10px] leading-4">
              Royal Palms Mall
              <br />
              Mfangano Street
              <br />
              +254795952128
              <br />
              novatech.co.ke
            </p>
          </div>

          <div className="border-b border-dashed border-black/20 py-3 text-[11px] leading-5">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold">Receipt</span>
              <span>#{data.order.id}</span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="font-semibold">Date</span>
              <span>{formatDateTime(data.order.addedOn)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="font-semibold">Status</span>
              <span>{data.order.status}</span>
            </div>
            {data.order.trackingNumber ? (
              <div className="mt-1 flex items-center justify-between gap-3">
                <span className="font-semibold">Ref</span>
                <span>{data.order.trackingNumber}</span>
              </div>
            ) : null}
          </div>

          <div className="border-b border-dashed border-black/20 py-3 text-[11px] leading-5">
            <p className="font-semibold">{data.order.deliveryName || "Walk-in customer"}</p>
            {data.order.deliveryPhone ? <p className="mt-1">{data.order.deliveryPhone}</p> : null}
            {data.order.email ? <p className="mt-1 break-words">{data.order.email}</p> : null}
            {data.order.deliveryAddress ? <p className="mt-1 whitespace-pre-line">{data.order.deliveryAddress}</p> : null}
          </div>

          <div className="py-3">
            <div className="grid grid-cols-[1fr_34px_68px] gap-2 border-b border-dashed border-black/20 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em]">
              <span>Item</span>
              <span className="text-right">Qty</span>
              <span className="text-right">Total</span>
            </div>
            <div className="space-y-3 py-3">
              {data.items.map((item) => (
                <div className="grid grid-cols-[1fr_34px_68px] gap-2 text-[11px] leading-4" key={item.id}>
                  <div className="min-w-0">
                    <p className="font-semibold">{item.name}</p>
                    <p className="mt-1 text-[10px]">x {formatCurrency(item.unitPrice)}</p>
                  </div>
                  <span className="text-right">{item.quantity}</span>
                  <span className="text-right font-semibold">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-dashed border-black/20 pt-3 text-[11px] leading-5">
            <div className="flex items-center justify-between gap-3">
              <span>Subtotal</span>
              <span>{formatCurrency(data.order.subtotal)}</span>
            </div>
            {data.order.discount > 0 ? (
              <div className="mt-1 flex items-center justify-between gap-3">
                <span>Discount</span>
                <span>-{formatCurrency(data.order.discount)}</span>
              </div>
            ) : null}
            <div className="mt-1 flex items-center justify-between gap-3">
              <span>Paid</span>
              <span>{formatCurrency(data.order.amountPaid)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-dashed border-black/20 pt-2 text-sm font-bold">
              <span>Total</span>
              <span>{formatCurrency(data.order.total)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3 text-sm font-bold">
              <span>Balance</span>
              <span>{formatCurrency(data.order.balance)}</span>
            </div>
          </div>

          {qrImageUrl ? (
            <div className="border-t border-dashed border-black/20 pt-3 text-center">
              <img alt={`QR code for receipt ${data.order.id}`} className="mx-auto h-[24mm] w-[24mm] object-contain" src={qrImageUrl} />
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em]">Scan receipt</p>
            </div>
          ) : null}

          <div className="border-t border-dashed border-black/20 pt-3 text-center text-[10px] leading-4">
            <p>Thank you for shopping with Novatech.</p>
            <p className="mt-1">You were served by {servedBy}.</p>
          </div>
        </div>
      )}
    </section>
  );
}
